import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isNullOrEmpty } from '@cyberint/common-utils';
import { FormHelperText, InputLabel, Select as MuiSelect } from '@mui/material';
import { CustomTooltip } from '../Poppers';
import Icons, { IconSizes } from '../Icons';
import { Themes } from '../../style/theme-consts';
import { stopPropagation } from '../../utils/listeners';
import { InputHeights } from '../../utils/consts';
import { DomUtils } from '../../utils/dom-utils';
import { RenderValueTypes } from './select-consts';
import { spreadHierarchicalSelection, buildRenderer, mapSubOptions } from './selectUtils';
import * as S from './styled';

const ExpandMore = value => props => <S.StyledExpandMore {...props} $hasSelectedValue={!isNullOrEmpty(value)} />;
const INPUT_PROPS = mode => ({
  MenuProps: {
    classes: { paper: mode },
    sx: { '.MuiPopover-paper': S.POPPER_STYLE },
    PaperProps: {
      sx: {
        '.MuiMenuItem-root:hover': { backgroundColor: 'var(--select-list-option-hover-bg)' },
        '.MuiMenuItem-root.Mui-selected': { backgroundColor: 'var(--select-list-option-focused-bg)' }
      }
    }
  }
});

const Select = ({
  value,
  options,
  onChange,
  onBlur,
  onOpen,
  open,
  onClose,
  onClick,
  disabled,
  width = '280px',
  height = InputHeights.MEDIUM,
  renderValue = RenderValueTypes.CHIPS,
  noBorder = false,
  multiple,
  withClear,
  clearable,
  className,
  label,
  error,
  loading,
  testId,
  helperText,
  optionOverrideCss,
  mode: modeProp,
  displayEmpty
}) => {
  const { theme, elementRef } = DomUtils.useDetectParentTheme();
  const mode = modeProp || theme || Themes.LIGHT;
  const hasHierarchy = useMemo(() => options.some(o => o.children?.length), [options]);
  const optionsByValue = useMemo(
    () => mapSubOptions(options).reduce((acc, o) => ({ ...acc, [o.value]: o }), {}),
    [options]
  );
  const onChangeHandler = useCallback(
    (e, ...args) => {
      const values = e.target.value;
      if (multiple && Array.isArray(values) && values.length > 0 && hasHierarchy) {
        e.target.value = spreadHierarchicalSelection({ values, optionsByValue });
        onChange?.(e, ...args, { originalValues: values });
      } else {
        onChange?.(e, ...args);
      }
    },
    [onChange, optionsByValue, hasHierarchy, multiple]
  );

  const clearSelectionHandler = e => {
    e.stopPropagation();
    onChange?.({ target: { value: multiple ? [] : null } }, null, { isClear: true });
  };

  const renderer = useMemo(() => {
    const clearValue = v => {
      const filtered = value?.filter(sv => sv !== v);
      const e = { target: { value: filtered } };
      onChange?.(e, null, { isClear: true });
    };
    return buildRenderer({ renderValue, value, multiple, optionsByValue, testId, clearValue });
  }, [renderValue, optionsByValue, onChange, value, multiple]);

  const inputProps = useMemo(() => INPUT_PROPS(mode), [mode]);

  return (
    <S.StyledWrapper
      ref={elementRef}
      $width={width}
      $height={height}
      $noBorder={noBorder}
      className={className}
      onClick={stopPropagation}>
      <S.StyledFormControl fullWidth error={error}>
        {label && <InputLabel id={error ? 'simple-select-error-label' : 'simple-select-label'}>{label}</InputLabel>}
        <MuiSelect
          labelId={error ? 'simple-select-error-label' : 'simple-select-label'}
          id={error ? 'simple-select-error' : 'simple-select'}
          value={value}
          label={label}
          onChange={onChangeHandler}
          onBlur={onBlur}
          onOpen={onOpen}
          open={open}
          onClose={onClose}
          onClick={onClick}
          disabled={disabled}
          renderValue={renderer}
          displayEmpty={displayEmpty}
          multiple={multiple}
          IconComponent={ExpandMore(value)}
          data-testid={testId}
          endAdornment={
            (withClear || clearable) &&
            (multiple ? !!value?.length : !!value) && <S.SelectClear onClick={clearSelectionHandler} />
          }
          inputProps={inputProps}>
          {mapSubOptions(options).map(option => {
            const selectedChildren = option.children?.filter(c => value?.includes(c.value))?.length;
            return (
              <S.StyledMenuItem
                key={option.key || (typeof option.label === 'string' ? option.label : option.value)}
                value={option.value}
                disabled={!!option.disabled}
                className={option.className}
                $sub={option.sub}>
                {multiple && (
                  <S.StyledCheckbox
                    checked={
                      value?.indexOf(option.value) > -1 ||
                      (selectedChildren ? selectedChildren === option.children?.length : false)
                    }
                    indeterminate={
                      selectedChildren ? selectedChildren < option.children?.length && selectedChildren > 0 : false
                    }
                    withPropagation
                  />
                )}
                <S.StyledOption $overrideCss={optionOverrideCss}>
                  {option.icon}
                  {option.dotColor && <S.ColoredCircle $color={option.dotColor} />}
                  {option.label}
                  {!!option.tooltip && (
                    <CustomTooltip title={option.tooltip} mode={mode} placement="right">
                      <S.TooltipWrapper>
                        <Icons.Info size={IconSizes.S} />
                      </S.TooltipWrapper>
                    </CustomTooltip>
                  )}
                </S.StyledOption>
              </S.StyledMenuItem>
            );
          })}
        </MuiSelect>
        {helperText && error && <FormHelperText>{helperText}</FormHelperText>}
        {loading && <S.LoadingIcon size={16} color="var(--button-loading-icon-color)" rotate />}
      </S.StyledFormControl>
    </S.StyledWrapper>
  );
};

Select.propTypes = {
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.any.isRequired,
      key: PropTypes.string,
      children: PropTypes.array,
      disabled: PropTypes.bool,
      dotColor: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  renderValue: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.oneOf([RenderValueTypes.CHIPS, RenderValueTypes.COMMA_SEPARATED, RenderValueTypes.COUNT])
  ]),
  onBlur: PropTypes.func,
  onOpen: PropTypes.func,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  withClear: PropTypes.bool,
  clearable: PropTypes.bool,
  className: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', PropTypes.string, PropTypes.number]),
  noBorder: PropTypes.bool,
  label: PropTypes.string,
  testId: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  loading: PropTypes.bool,
  optionOverrideCss: PropTypes.object,
  mode: PropTypes.oneOf([Themes.DARK, Themes.LIGHT]),
  displayEmpty: PropTypes.bool
};

export default Select;
